import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CustomLoading } from "components/CustomLoading";
import { Check, X, FileText, FilePlus, Pencil, AlertTriangle, UserX } from "lucide-react";
import { User, loadUsers, displayName, avatarColor, initials } from "./user-display";
import { formatRelative } from "./category-rows";
import ReviewProposalModal from "./ReviewProposalModal";
import PermissionsPanel from "./PermissionsPanel";

interface Category {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
}

interface Page {
    id: string;
    categoryId: string;
    folderId: string | null;
    title: string;
    body: string | null;
    ownerId: string | null;
    updatedAt: string;
    status: string;
}

interface Proposal {
    id: string;
    pageId: string | null;
    categoryId: string | null;
    authorId: string;
    proposalType: "edit" | "update_request" | "new_page_request";
    proposedBody: string | null;
    requestedTitle: string | null;
    requestedBody: string | null;
    note: string | null;
    status: string;
    reviewedBy: string | null;
    reviewedAt: string | null;
    reviewNote: string | null;
    createdAt: string;
    createdBy: string;
}

type TabKey = "requests" | "stale" | "unowned" | "permissions";
type StatusFilter = "open" | "accepted" | "rejected" | "all";
type TypeFilter = "all" | "edit" | "update_request" | "new_page_request";

interface Props {
    categories: Category[];
    isAdmin: boolean;
    canManage: boolean;
    onSelectPage: (pageId: string, categoryId: string) => void;
    onSelectCategory: (categoryId: string) => void;
    onReviewEdit: (page: Page, proposedBody: string, proposalId: string) => void;
}

export default function KnowledgeManagementView({ categories, isAdmin, canManage, onSelectPage, onSelectCategory, onReviewEdit }: Props) {
    // Admins get the full management view; editors get a scoped Page Requests inbox
    // (only requests on pages they own — the backend enforces the scoping).
    const canAccess = isAdmin || canManage;
    const editorOnly = canManage && !isAdmin;
    const [tab, setTab] = useState<TabKey>("requests");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
    const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

    const [loading, setLoading] = useState(false);
    const [proposals, setProposals] = useState<Proposal[]>([]);
    const [stale, setStale] = useState<Page[]>([]);
    const [unowned, setUnowned] = useState<Page[]>([]);
    const [pagesById, setPagesById] = useState<Map<string, Page>>(new Map());
    const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map());

    const [reviewTarget, setReviewTarget] = useState<{ proposal: Proposal; mode: "accept" | "reject" } | null>(null);

    const categoriesById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);

    useEffect(() => {
        if (!canAccess) return;
        loadUsers().then(users => setUsersMap(new Map(users.map(u => [u.id, u]))));
    }, [canAccess]);

    useEffect(() => {
        if (!canAccess) return;
        if (tab === "requests") loadProposals();
        if (isAdmin && tab === "stale") loadStale();
        if (isAdmin && tab === "unowned") loadUnowned();
    }, [tab, statusFilter, typeFilter, canAccess, isAdmin]);

    function loadProposals() {
        setLoading(true);
        const statusParam = statusFilter === "all" ? "" : `&status=${statusFilter}`;
        const typeParam = typeFilter === "all" ? "" : `&type=${typeFilter}`;
        axios.get(`/api/manuals/edit-proposals?take=100${statusParam}${typeParam}`)
            .then(async res => {
                if (!res.data.success) {
                    toast.error(res.data.message || "Failed to load requests");
                    setProposals([]);
                    return;
                }
                const items: Proposal[] = res.data.items;
                setProposals(items);
                const pageIds = Array.from(new Set(items.map(p => p.pageId).filter((x): x is string => !!x)));
                if (pageIds.length > 0) {
                    const results = await Promise.all(
                        pageIds.map(id =>
                            axios.get(`/api/manuals/pages/${id}`).then(r => r.data.success ? r.data.item as Page : null).catch(() => null)
                        )
                    );
                    const m = new Map<string, Page>();
                    results.forEach(p => { if (p) m.set(p.id, p); });
                    setPagesById(prev => {
                        const next = new Map(prev);
                        m.forEach((v, k) => next.set(k, v));
                        return next;
                    });
                }
            })
            .catch(() => toast.error("Failed to load requests"))
            .finally(() => setLoading(false));
    }

    function loadStale() {
        setLoading(true);
        axios.get("/api/manuals/pages/stale?days=90")
            .then(res => {
                if (res.data.success) setStale(res.data.items);
                else toast.error(res.data.message || "Failed to load stale pages");
            })
            .catch(() => toast.error("Failed to load stale pages"))
            .finally(() => setLoading(false));
    }

    function loadUnowned() {
        setLoading(true);
        axios.get("/api/manuals/pages/unowned")
            .then(res => {
                if (res.data.success) setUnowned(res.data.items);
                else toast.error(res.data.message || "Failed to load unowned pages");
            })
            .catch(() => toast.error("Failed to load unowned pages"))
            .finally(() => setLoading(false));
    }

    function handleReview(proposal: Proposal, mode: "accept" | "reject") {
        setReviewTarget({ proposal, mode });
    }

    function submitReview(reviewNote: string | null) {
        if (!reviewTarget) return;
        const { proposal, mode } = reviewTarget;
        setReviewTarget(null);
        axios.post(`/api/manuals/edit-proposals/${proposal.id}/${mode}`, { reviewNote })
            .then(res => {
                if (res.data.success) {
                    toast.success(mode === "accept" ? "Request accepted" : "Request rejected");
                    loadProposals();
                } else {
                    toast.error(res.data.message || `Failed to ${mode}`);
                }
            })
            .catch(err => toast.error(err.response?.data?.message || `Failed to ${mode}`));
    }

    if (!canAccess) {
        return (
            <div className="m-cat-page">
                <div className="m-cat-header">
                    <div><h1 className="m-cat-title">Page Requests</h1></div>
                </div>
                <div className="m-profile-empty">You don't have access to this view.</div>
            </div>
        );
    }

    // editors always sit on the requests view; only admins switch tabs
    const activeTab: TabKey = editorOnly ? "requests" : tab;

    return (
        <div className="m-cat-page">
            <div className="m-cat-header">
                <div><h1 className="m-cat-title">{isAdmin ? "Permission/Access Management" : "Page Requests"}</h1></div>
            </div>

            {editorOnly ? (
                <div className="m-profile-empty" style={{ textAlign: "left", padding: "0 0 12px" }}>
                    Requests on pages you own.
                </div>
            ) : (
                <div className="m-profile-tabs">
                    <button
                        className={`m-cat-tab${tab === "requests" ? " active" : ""}`}
                        onClick={() => setTab("requests")}
                    >
                        <span>Page requests</span>
                        <span className="m-cat-tab-count">{proposals.length}</span>
                    </button>
                    <button
                        className={`m-cat-tab${tab === "stale" ? " active" : ""}`}
                        onClick={() => setTab("stale")}
                    >
                        <span>Stale pages</span>
                        <span className="m-cat-tab-count">{stale.length}</span>
                    </button>
                    <button
                        className={`m-cat-tab${tab === "unowned" ? " active" : ""}`}
                        onClick={() => setTab("unowned")}
                    >
                        <span>Unowned pages</span>
                        <span className="m-cat-tab-count">{unowned.length}</span>
                    </button>
                    <button
                        className={`m-cat-tab${tab === "permissions" ? " active" : ""}`}
                        onClick={() => setTab("permissions")}
                    >
                        <span>Permissions</span>
                    </button>
                </div>
            )}

            <div className="m-profile-body">
                {activeTab === "requests" && (
                    <div className="m-km-filter-row">
                        <select
                            className="m-input m-km-filter"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                        >
                            <option value="open">Open</option>
                            <option value="accepted">Accepted</option>
                            <option value="rejected">Rejected</option>
                            <option value="all">All</option>
                        </select>
                        <select
                            className="m-input m-km-filter"
                            value={typeFilter}
                            onChange={e => setTypeFilter(e.target.value as TypeFilter)}
                        >
                            <option value="all">All types</option>
                            <option value="edit">Edit</option>
                            <option value="update_request">Update page</option>
                            <option value="new_page_request">New page</option>
                        </select>
                    </div>
                )}

                {loading ? (
                    <div className="m-loading"><CustomLoading /></div>
                ) : activeTab === "permissions" ? (
                    <PermissionsPanel />
                ) : activeTab === "requests" ? (
                    <ProposalList
                        proposals={proposals}
                        pagesById={pagesById}
                        categoriesById={categoriesById}
                        usersMap={usersMap}
                        onReview={handleReview}
                        onReviewEdit={onReviewEdit}
                        onSelectPage={onSelectPage}
                        onSelectCategory={onSelectCategory}
                    />
                ) : activeTab === "stale" ? (
                    <PageTable
                        pages={stale}
                        categoriesById={categoriesById}
                        usersMap={usersMap}
                        emptyLabel="No stale pages. Everything is up to date."
                        onSelectPage={onSelectPage}
                    />
                ) : (
                    <PageTable
                        pages={unowned}
                        categoriesById={categoriesById}
                        usersMap={usersMap}
                        emptyLabel="Every published page has an owner."
                        onSelectPage={onSelectPage}
                        hideOwner
                    />
                )}
            </div>

            {reviewTarget && (
                <ReviewProposalModal
                    title={reviewTarget.mode === "accept" ? "Accept request" : "Reject request"}
                    subtitle={proposalTitle(reviewTarget.proposal, pagesById)}
                    confirmLabel={reviewTarget.mode === "accept" ? "Accept" : "Reject"}
                    danger={reviewTarget.mode === "reject"}
                    warning={reviewTarget.mode === "accept" ? acceptWarning(reviewTarget.proposal) : null}
                    onConfirm={submitReview}
                    onCancel={() => setReviewTarget(null)}
                />
            )}
        </div>
    );
}

function ProposalList({
    proposals,
    pagesById,
    categoriesById,
    usersMap,
    onReview,
    onReviewEdit,
    onSelectPage,
    onSelectCategory,
}: {
    proposals: Proposal[];
    pagesById: Map<string, Page>;
    categoriesById: Map<string, Category>;
    usersMap: Map<string, User>;
    onReview: (p: Proposal, mode: "accept" | "reject") => void;
    onReviewEdit: (page: Page, proposedBody: string, proposalId: string) => void;
    onSelectPage: (pageId: string, categoryId: string) => void;
    onSelectCategory: (categoryId: string) => void;
}) {
    if (proposals.length === 0) {
        return <div className="m-profile-empty">No requests in this view.</div>;
    }

    return (
        <div className="m-km-request-list">
            {proposals.map(p => {
                const page = p.pageId ? pagesById.get(p.pageId) : null;
                const cat = p.categoryId
                    ? categoriesById.get(p.categoryId)
                    : page ? categoriesById.get(page.categoryId) : null;
                const author = usersMap.get(p.authorId);
                const authorName = author ? displayName(author) : p.authorId.slice(0, 8);
                const reviewer = p.reviewedBy ? usersMap.get(p.reviewedBy) : null;
                const reviewerName = reviewer ? displayName(reviewer) : (p.reviewedBy ? p.reviewedBy.slice(0, 8) : null);
                const title = proposalTitle(p, pagesById);
                const isOpen = p.status === "open";
                return (
                    <div key={p.id} className="m-km-request-row">
                        <span className="m-km-request-icon" title={proposalTypeLabel(p.proposalType)}>
                            {p.proposalType === "new_page_request" ? <FilePlus size={18} /> :
                             p.proposalType === "update_request" ? <AlertTriangle size={18} /> :
                             <Pencil size={18} />}
                        </span>
                        <div className="m-km-request-main">
                            <button
                                className="m-km-request-title"
                                onClick={() => {
                                    if (page) onSelectPage(page.id, page.categoryId);
                                    else if (cat) onSelectCategory(cat.id);
                                }}
                            >
                                {title}
                            </button>
                            <div className="m-km-request-meta">
                                <span className="m-km-request-type">{proposalTypeLabel(p.proposalType)}</span>
                                <span>·</span>
                                <span className="m-comment-avatar sm" style={{ background: avatarColor(p.authorId) }}>
                                    {initials(authorName)}
                                </span>
                                <span>{authorName}</span>
                                {cat && <><span>·</span><span style={{ color: cat.color || undefined }}>{cat.name}</span></>}
                                <span>·</span>
                                <span>Opened {formatRelative(p.createdAt)}</span>
                                {!isOpen && <><span>·</span><span className={`m-km-status m-km-status-${p.status}`}>{p.status}</span></>}
                            </div>
                            {p.note && <div className="m-km-request-note">{p.note}</div>}
                            {p.proposalType === "edit" && p.proposedBody && (
                                <details className="m-km-request-details">
                                    <summary>Compare changes</summary>
                                    <div className="m-km-compare">
                                        <div className="m-km-compare-col">
                                            <div className="m-km-compare-label">Current page</div>
                                            <div
                                                className="m-page-body m-km-request-body"
                                                dangerouslySetInnerHTML={{ __html: page?.body || "<p><em>(empty)</em></p>" }}
                                            />
                                        </div>
                                        <div className="m-km-compare-col">
                                            <div className="m-km-compare-label">Suggested</div>
                                            <div
                                                className="m-page-body m-km-request-body"
                                                dangerouslySetInnerHTML={{ __html: p.proposedBody }}
                                            />
                                        </div>
                                    </div>
                                </details>
                            )}
                            {p.proposalType === "new_page_request" && p.requestedBody && (
                                <details className="m-km-request-details">
                                    <summary>What should it cover?</summary>
                                    <div className="m-km-request-body m-km-request-body-text">{p.requestedBody}</div>
                                </details>
                            )}
                            {!isOpen && (p.reviewNote || reviewerName) && (
                                <div className={`m-km-request-review m-km-request-review-${p.status}`}>
                                    <div className="m-km-request-review-meta">
                                        Reviewed{reviewerName ? ` by ${reviewerName}` : ""}
                                        {p.reviewedAt ? ` · ${formatRelative(p.reviewedAt)}` : ""}
                                    </div>
                                    {p.reviewNote && <div className="m-km-request-review-note">{p.reviewNote}</div>}
                                </div>
                            )}
                        </div>
                        {isOpen && (
                            <div className="m-km-request-actions">
                                {p.proposalType === "edit" ? (
                                    <button
                                        className="m-btn m-btn-ghost m-btn-sm"
                                        disabled={!page || !p.proposedBody}
                                        title={!page ? "Loading page…" : undefined}
                                        onClick={() => { if (page && p.proposedBody) onReviewEdit(page, p.proposedBody, p.id); }}
                                    >
                                        <Pencil size={13} /> Review &amp; apply
                                    </button>
                                ) : (
                                    <button className="m-btn m-btn-ghost m-btn-sm" onClick={() => onReview(p, "accept")}>
                                        <Check size={13} /> Accept
                                    </button>
                                )}
                                <button className="m-btn m-btn-ghost m-btn-sm" onClick={() => onReview(p, "reject")}>
                                    <X size={13} /> Reject
                                </button>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function PageTable({
    pages,
    categoriesById,
    usersMap,
    emptyLabel,
    onSelectPage,
    hideOwner = false,
}: {
    pages: Page[];
    categoriesById: Map<string, Category>;
    usersMap: Map<string, User>;
    emptyLabel: string;
    onSelectPage: (pageId: string, categoryId: string) => void;
    hideOwner?: boolean;
}) {
    if (pages.length === 0) {
        return <div className="m-profile-empty">{emptyLabel}</div>;
    }
    return (
        <div className="m-profile-table">
            <div className="m-profile-table-head">
                <span>Name</span>
                <span>Type</span>
            </div>
            {pages.map(p => {
                const cat = categoriesById.get(p.categoryId);
                const owner = p.ownerId ? usersMap.get(p.ownerId) : null;
                const ownerLabel = hideOwner
                    ? null
                    : owner ? displayName(owner) : p.ownerId ? p.ownerId.slice(0, 8) : "—";
                const metaParts: string[] = [];
                if (cat) metaParts.push(cat.name);
                if (ownerLabel) metaParts.push(`Owner: ${ownerLabel}`);
                metaParts.push(`Updated ${formatRelative(p.updatedAt)}`);
                return (
                    <div
                        key={p.id}
                        className="m-profile-table-row clickable"
                        onClick={() => onSelectPage(p.id, p.categoryId)}
                        role="button"
                    >
                        <div className="m-profile-row-main">
                            <span className="m-profile-row-icon">
                                {hideOwner ? <UserX size={18} className="icon" color="#f59e0b" /> : <FileText size={18} className="icon" color="#1e85e5" />}
                            </span>
                            <div className="m-profile-row-text">
                                <div className="m-profile-row-title">{p.title || "Untitled"}</div>
                                <div className="m-profile-row-meta">{metaParts.join(" · ")}</div>
                            </div>
                        </div>
                        <div className="m-profile-row-type">{hideOwner ? "Unowned" : "Stale"}</div>
                    </div>
                );
            })}
        </div>
    );
}

function proposalTypeLabel(t: string): string {
    if (t === "edit") return "Edit request";
    if (t === "update_request") return "Update page";
    if (t === "new_page_request") return "New page";
    return "Request";
}

function acceptWarning(p: Proposal): { tone: "danger" | "info"; text: string } | null {
    switch (p.proposalType) {
        case "edit":
            return {
                tone: "danger",
                text: "Accepting immediately replaces the entire page body with the suggested content — the page is re-saved using only what's in this request, so anything the requester changed or removed will be applied as-is. Review the suggested body carefully before accepting.",
            };
        case "update_request":
            return {
                tone: "info",
                text: "Accepting does not change the page — you'll need to make the update yourself. These submitted details are only visible while the request is pending; once accepted it moves to Completed Requests and the details are no longer shown.",
            };
        case "new_page_request":
            return {
                tone: "info",
                text: "Accepting does not create the page — create the page first, then accept this request. The submitted content is only visible while the request is pending; once accepted it moves to Completed Requests and is no longer shown.",
            };
        default:
            return null;
    }
}

function proposalTitle(p: Proposal, pagesById: Map<string, Page>): string {
    if (p.proposalType === "new_page_request") {
        return p.requestedTitle ? `New page: "${p.requestedTitle}"` : "New page request";
    }
    const page = p.pageId ? pagesById.get(p.pageId) : null;
    const pageTitle = page?.title || "(page)";
    return p.proposalType === "edit" ? `Edit "${pageTitle}"` : `Update "${pageTitle}"`;
}
